import {
  ACTIVITY_PUSH_KINDS,
  CARD_PERIODS,
  GROUP_EVENT_KINDS,
  NOTIFICATION_KINDS,
  PUSH_CATEGORIES,
  REACTION_KINDS,
  UPDATE_KINDS,
  isTimeOfDay,
  type PushKind,
} from "@proef/domain";
import { z } from "zod";
import { PublicUser } from "./friendship.js";
import { GroupRole } from "./group.js";
import { InviteView } from "./invite.js";
import type { PushLang, PushPayload } from "./push.js";
import { TastingSessionStatus } from "./tastingSession.js";

export const NotificationKind = z.enum(NOTIFICATION_KINDS);
export type NotificationKind = z.infer<typeof NotificationKind>;
export const PushCategory = z.enum(PUSH_CATEGORIES);
export type PushCategory = z.infer<typeof PushCategory>;

const TimeOfDay = z.string().refine(isTimeOfDay, "expected HH:MM, 24-hour");
export const QuietHours = z.object({ start: TimeOfDay, end: TimeOfDay });
export type QuietHours = z.infer<typeof QuietHours>;

/** GET /v1/notifications/prefs. `quietHours: null` switches quiet hours off. */
export const NotificationPrefs = z.object({
  push: z.object({ invites: z.boolean(), sessions: z.boolean(), activity: z.boolean() }),
  quietHours: QuietHours.nullable(),
  /** The device's IANA zone, so quiet hours follow the person's own clock. */
  timeZone: z.string().min(1).max(64),
});
export type NotificationPrefs = z.infer<typeof NotificationPrefs>;

/** PUT /v1/notifications/prefs -- only what changes. */
export const UpdateNotificationPrefsRequest = z.object({
  push: NotificationPrefs.shape.push.partial().optional(),
  quietHours: QuietHours.nullable().optional(),
  timeZone: z.string().min(1).max(64).optional(),
});
export type UpdateNotificationPrefsRequest = z.infer<typeof UpdateNotificationPrefsRequest>;

export const MuteScope = z.enum(["user", "group"]);
export type MuteScope = z.infer<typeof MuteScope>;

/** A muted person or group still reaches the inbox; only their pushes stop. */
export const MuteView = z.object({
  scope: MuteScope,
  subjectId: z.string().min(1),
  name: z.string(),
  createdAt: z.iso.datetime(),
});
export type MuteView = z.infer<typeof MuteView>;

export const MuteList = z.object({ items: z.array(MuteView) });
export type MuteList = z.infer<typeof MuteList>;

const GroupRef = z.object({ id: z.string().min(1), name: z.string() });
const SessionRef = z.object({
  id: z.string().min(1),
  name: z.string(),
  status: TastingSessionStatus,
  scheduledAt: z.iso.datetime().nullable(),
});

/** Something waiting on the viewer. `key` is what a push's deep link names (domain inboxItemKey). */
export const ReceivedItem = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("friend_request"),
    key: z.string(),
    friendshipId: z.string().min(1),
    user: PublicUser,
    createdAt: z.iso.datetime(),
  }),
  z.object({
    kind: z.literal("group_invite"),
    key: z.string(),
    group: GroupRef,
    invitedBy: PublicUser.nullable(),
    createdAt: z.iso.datetime(),
  }),
  z.object({
    kind: z.literal("group_join_request"),
    key: z.string(),
    group: GroupRef,
    user: PublicUser,
    createdAt: z.iso.datetime(),
  }),
  z.object({
    kind: z.literal("session_invite"),
    key: z.string(),
    session: SessionRef,
    host: PublicUser,
    createdAt: z.iso.datetime(),
  }),
]);
export type ReceivedItem = z.infer<typeof ReceivedItem>;

/** Something the viewer asked of someone else, still unanswered. */
export const SentItem = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("friend_request"),
    key: z.string(),
    friendshipId: z.string().min(1),
    user: PublicUser,
    createdAt: z.iso.datetime(),
  }),
  z.object({
    kind: z.literal("group_invite"),
    key: z.string(),
    group: GroupRef,
    user: PublicUser,
    /** A direct invite can be nudged once. */
    remindedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  }),
]);
export type SentItem = z.infer<typeof SentItem>;

/** What a reaction notification points at. `reaction` is null once the reaction was taken back. */
export const CardRef = z.object({
  id: z.string().min(1),
  period: z.enum(CARD_PERIODS),
  periodKey: z.string().min(1).max(10),
  reaction: z.enum(REACTION_KINDS).nullable(),
});
export type CardRef = z.infer<typeof CardRef>;

/** Read-only news, kept a month. */
export const UpdateItem = z.object({
  id: z.string().min(1),
  kind: z.enum(UPDATE_KINDS),
  actor: PublicUser.nullable(),
  group: GroupRef.nullable(),
  /** The session a reminder, move or cancellation is about. */
  session: SessionRef.nullable(),
  /** The card a reaction is about, while it still exists. */
  card: CardRef.nullable(),
  read: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type UpdateItem = z.infer<typeof UpdateItem>;

/** GET /v1/inbox/summary -- what the tab badge shows: open questions plus unread news. */
export const InboxSummary = z.object({
  received: z.int().nonnegative(),
  unreadUpdates: z.int().nonnegative(),
  badge: z.int().nonnegative(),
});
export type InboxSummary = z.infer<typeof InboxSummary>;

/** GET /v1/inbox */
export const Inbox = z.object({
  received: z.array(ReceivedItem),
  sent: z.array(SentItem),
  /** The viewer's own invite links, live and recently ended. */
  links: z.array(InviteView),
  updates: z.array(UpdateItem),
  summary: InboxSummary,
});
export type Inbox = z.infer<typeof Inbox>;

/** POST /v1/inbox/read -- marks updates up to `before` (default: now) read. */
export const MarkInboxReadRequest = z.object({ before: z.iso.datetime().optional() });
export type MarkInboxReadRequest = z.infer<typeof MarkInboxReadRequest>;

/** POST /v1/groups/:id/invites -- who got a new invite; people already in, asked or invited are
 * skipped silently, so a repeat tap is harmless. */
export const InviteToGroupResponse = z.object({ invited: z.array(z.string().min(1)) });
export type InviteToGroupResponse = z.infer<typeof InviteToGroupResponse>;

export const GroupEventKind = z.enum(GROUP_EVENT_KINDS);
export type GroupEventKind = z.infer<typeof GroupEventKind>;

/** One line of a group's log. `subject` is the member it is about, `role` set for role changes, and
 * `goal` (a reached weekly goal) names the week and the target. */
export const GroupEventView = z.object({
  id: z.string().min(1),
  kind: GroupEventKind,
  actor: PublicUser.nullable(),
  subject: PublicUser.nullable(),
  role: GroupRole.nullable(),
  /** Only on a `goal_reached` line; absent elsewhere, so older rows and clients read as before. */
  goal: z.object({ week: z.string().min(1).max(10), target: z.int().positive() }).exactOptional(),
  /** On the session lines (planned, yes, started, result, tasting now): the session they are about. */
  session: z.object({ id: z.string().min(1), name: z.string() }).exactOptional(),
  /** On a `session_result` line only: how it came out, counts and nothing a person wrote. */
  result: z
    .object({ wines: z.int().nonnegative(), people: z.int().nonnegative(), blind: z.boolean() })
    .exactOptional(),
  createdAt: z.iso.datetime(),
});
export type GroupEventView = z.infer<typeof GroupEventView>;

/** GET /v1/groups/:id/events */
export const GroupEventList = z.object({
  items: z.array(GroupEventView),
  nextCursor: z.string().nullable(),
});
export type GroupEventList = z.infer<typeof GroupEventList>;

/** An app route inside the PWA, never another origin: the service worker opens it on a tap. */
const AppRoute = z
  .string()
  .max(512)
  .refine((url) => url.startsWith("/") && !url.startsWith("//"), "expected an app route");

/** What a social push carries; the service worker shows `title`/`body` and opens `url`. */
export const SocialPushPayload = z.object({
  kind: z.enum([...NOTIFICATION_KINDS, ...ACTIVITY_PUSH_KINDS]),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(200),
  url: AppRoute,
  tag: z.string().min(1).max(200),
});
export type SocialPushPayload = z.infer<typeof SocialPushPayload>;

export interface SocialPushNames {
  readonly actor: string;
  readonly group?: string | undefined;
  readonly session?: string | undefined;
}

type PushText = (names: SocialPushNames) => PushPayload;

const NL: Readonly<Record<PushKind, PushText>> = {
  friend_request: ({ actor }) => ({
    title: "Vriendschapsverzoek",
    body: `${actor} wil samen met je proeven.`,
  }),
  group_invite: ({ actor, group }) => ({
    title: `Uitnodiging voor ${group ?? "een groep"}`,
    body: `${actor} nodigt je uit voor ${group ?? "een groep"}.`,
  }),
  group_join_request: ({ actor, group }) => ({
    title: `Aanvraag voor ${group ?? "je groep"}`,
    body: `${actor} wil lid worden van ${group ?? "je groep"}.`,
  }),
  session_invite: ({ actor, session }) => ({
    title: "Uitnodiging voor een proeverij",
    body: `${actor} nodigt je uit voor ${session ?? "een proeverij"}.`,
  }),
  session_reminder: ({ session }) => ({
    title: "Over een uur proeven",
    body: `${session ?? "Je proeverij"} begint over een uur.`,
  }),
  session_starting: ({ session }) => ({
    title: "De proeverij begint",
    body: `${session ?? "Je proeverij"} begint nu.`,
  }),
  session_moved: ({ session }) => ({
    title: "Nieuwe tijd voor een proeverij",
    body: `${session ?? "Je proeverij"} is verplaatst.`,
  }),
  session_cancelled: ({ session }) => ({
    title: "Proeverij afgelast",
    body: `${session ?? "Je proeverij"} gaat niet door.`,
  }),
  activity_tasting_now: ({ actor, session }) => ({
    title: `${actor} is aan het proeven`,
    body: `${actor} proeft nu ${session ?? "samen met anderen"}.`,
  }),
  group_shop_tick: ({ actor, group }) => ({
    title: group ?? "een groep",
    body: `${actor} heeft een wijn op de lijst.`,
  }),
};

const EN: Readonly<Record<PushKind, PushText>> = {
  friend_request: ({ actor }) => ({
    title: "Friend request",
    body: `${actor} wants to taste wine with you.`,
  }),
  group_invite: ({ actor, group }) => ({
    title: `Invitation to ${group ?? "a group"}`,
    body: `${actor} invites you to ${group ?? "a group"}.`,
  }),
  group_join_request: ({ actor, group }) => ({
    title: `Request for ${group ?? "your group"}`,
    body: `${actor} wants to join ${group ?? "your group"}.`,
  }),
  session_invite: ({ actor, session }) => ({
    title: "Invitation to a tasting",
    body: `${actor} invites you to ${session ?? "a tasting"}.`,
  }),
  session_reminder: ({ session }) => ({
    title: "Tasting in an hour",
    body: `${session ?? "Your tasting"} starts in an hour.`,
  }),
  session_starting: ({ session }) => ({
    title: "Your tasting is starting",
    body: `${session ?? "Your tasting"} starts now.`,
  }),
  session_moved: ({ session }) => ({
    title: "New time for a tasting",
    body: `${session ?? "Your tasting"} has moved.`,
  }),
  session_cancelled: ({ session }) => ({
    title: "Tasting cancelled",
    body: `${session ?? "Your tasting"} is not going ahead.`,
  }),
  activity_tasting_now: ({ actor, session }) => ({
    title: `${actor} is tasting now`,
    body: `${actor} is tasting ${session ?? "with others"} right now.`,
  }),
  group_shop_tick: ({ actor, group }) => ({
    title: group ?? "a group",
    body: `${actor} has a wine on the list.`,
  }),
};

const DE: Readonly<Record<PushKind, PushText>> = {
  friend_request: ({ actor }) => ({
    title: "Freundschaftsanfrage",
    body: `${actor} möchte mit dir Wein probieren.`,
  }),
  group_invite: ({ actor, group }) => ({
    title: `Einladung zu ${group ?? "einer Gruppe"}`,
    body: `${actor} lädt dich zu ${group ?? "einer Gruppe"} ein.`,
  }),
  group_join_request: ({ actor, group }) => ({
    title: `Anfrage für ${group ?? "deine Gruppe"}`,
    body: `${actor} möchte ${group ?? "deiner Gruppe"} beitreten.`,
  }),
  session_invite: ({ actor, session }) => ({
    title: "Einladung zu einer Verkostung",
    body: `${actor} lädt dich zu ${session ?? "einer Verkostung"} ein.`,
  }),
  session_reminder: ({ session }) => ({
    title: "Verkostung in einer Stunde",
    body: `${session ?? "Deine Verkostung"} beginnt in einer Stunde.`,
  }),
  session_starting: ({ session }) => ({
    title: "Deine Verkostung beginnt",
    body: `${session ?? "Deine Verkostung"} beginnt jetzt.`,
  }),
  session_moved: ({ session }) => ({
    title: "Neue Zeit für eine Verkostung",
    body: `${session ?? "Deine Verkostung"} wurde verschoben.`,
  }),
  session_cancelled: ({ session }) => ({
    title: "Verkostung abgesagt",
    body: `${session ?? "Deine Verkostung"} findet nicht statt.`,
  }),
  activity_tasting_now: ({ actor, session }) => ({
    title: `${actor} verkostet gerade`,
    body: `${actor} verkostet jetzt ${session ?? "mit anderen"}.`,
  }),
  group_shop_tick: ({ actor, group }) => ({
    title: group ?? "einer Gruppe",
    body: `${actor} hat einen Wein auf der Liste.`,
  }),
};

const FR: Readonly<Record<PushKind, PushText>> = {
  friend_request: ({ actor }) => ({
    title: "Demande d'ami",
    body: `${actor} veut déguster du vin avec toi.`,
  }),
  group_invite: ({ actor, group }) => ({
    title: `Invitation à ${group ?? "un groupe"}`,
    body: `${actor} t'invite à ${group ?? "un groupe"}.`,
  }),
  group_join_request: ({ actor, group }) => ({
    title: `Demande pour ${group ?? "ton groupe"}`,
    body: `${actor} veut rejoindre ${group ?? "ton groupe"}.`,
  }),
  session_invite: ({ actor, session }) => ({
    title: "Invitation à une dégustation",
    body: `${actor} t'invite à ${session ?? "une dégustation"}.`,
  }),
  session_reminder: ({ session }) => ({
    title: "Dégustation dans une heure",
    body: `${session ?? "Ta dégustation"} commence dans une heure.`,
  }),
  session_starting: ({ session }) => ({
    title: "Ta dégustation commence",
    body: `${session ?? "Ta dégustation"} commence maintenant.`,
  }),
  session_moved: ({ session }) => ({
    title: "Nouvel horaire pour une dégustation",
    body: `${session ?? "Ta dégustation"} a été déplacée.`,
  }),
  session_cancelled: ({ session }) => ({
    title: "Dégustation annulée",
    body: `${session ?? "Ta dégustation"} n'aura pas lieu.`,
  }),
  activity_tasting_now: ({ actor, session }) => ({
    title: `${actor} déguste en ce moment`,
    body: `${actor} déguste maintenant ${session ?? "avec d'autres"}.`,
  }),
  group_shop_tick: ({ actor, group }) => ({
    title: group ?? "d'un groupe",
    body: `${actor} a un vin sur la liste.`,
  }),
};

const ES: Readonly<Record<PushKind, PushText>> = {
  friend_request: ({ actor }) => ({
    title: "Solicitud de amistad",
    body: `${actor} quiere catar vino contigo.`,
  }),
  group_invite: ({ actor, group }) => ({
    title: `Invitación a ${group ?? "un grupo"}`,
    body: `${actor} te invita a ${group ?? "un grupo"}.`,
  }),
  group_join_request: ({ actor, group }) => ({
    title: `Solicitud para ${group ?? "tu grupo"}`,
    body: `${actor} quiere unirse a ${group ?? "tu grupo"}.`,
  }),
  session_invite: ({ actor, session }) => ({
    title: "Invitación a una cata",
    body: `${actor} te invita a ${session ?? "una cata"}.`,
  }),
  session_reminder: ({ session }) => ({
    title: "Cata dentro de una hora",
    body: `${session ?? "Tu cata"} empieza dentro de una hora.`,
  }),
  session_starting: ({ session }) => ({
    title: "Tu cata empieza",
    body: `${session ?? "Tu cata"} empieza ahora.`,
  }),
  session_moved: ({ session }) => ({
    title: "Nuevo horario para una cata",
    body: `${session ?? "Tu cata"} se ha aplazado.`,
  }),
  session_cancelled: ({ session }) => ({
    title: "Cata cancelada",
    body: `${session ?? "Tu cata"} no se celebrará.`,
  }),
  activity_tasting_now: ({ actor, session }) => ({
    title: `${actor} está catando ahora`,
    body: `${actor} está catando ${session ?? "con otras personas"} en este momento.`,
  }),
  group_shop_tick: ({ actor, group }) => ({
    title: group ?? "un grupo",
    body: `${actor} tiene un vino en la lista.`,
  }),
};

const IT: Readonly<Record<PushKind, PushText>> = {
  friend_request: ({ actor }) => ({
    title: "Richiesta di amicizia",
    body: `${actor} vuole degustare vino con te.`,
  }),
  group_invite: ({ actor, group }) => ({
    title: `Invito a ${group ?? "un gruppo"}`,
    body: `${actor} ti invita a ${group ?? "un gruppo"}.`,
  }),
  group_join_request: ({ actor, group }) => ({
    title: `Richiesta per ${group ?? "il tuo gruppo"}`,
    body: `${actor} vuole unirsi a ${group ?? "il tuo gruppo"}.`,
  }),
  session_invite: ({ actor, session }) => ({
    title: "Invito a una degustazione",
    body: `${actor} ti invita a ${session ?? "una degustazione"}.`,
  }),
  session_reminder: ({ session }) => ({
    title: "Degustazione tra un'ora",
    body: `${session ?? "La tua degustazione"} inizia tra un'ora.`,
  }),
  session_starting: ({ session }) => ({
    title: "La tua degustazione inizia",
    body: `${session ?? "La tua degustazione"} inizia adesso.`,
  }),
  session_moved: ({ session }) => ({
    title: "Nuovo orario per una degustazione",
    body: `${session ?? "La tua degustazione"} è stata spostata.`,
  }),
  session_cancelled: ({ session }) => ({
    title: "Degustazione annullata",
    body: `${session ?? "La tua degustazione"} non si terrà.`,
  }),
  activity_tasting_now: ({ actor, session }) => ({
    title: `${actor} sta degustando`,
    body: `${actor} sta degustando ${session ?? "con altri"} proprio ora.`,
  }),
  group_shop_tick: ({ actor, group }) => ({
    title: group ?? "un gruppo",
    body: `${actor} ha un vino nella lista.`,
  }),
};

const PT: Readonly<Record<PushKind, PushText>> = {
  friend_request: ({ actor }) => ({
    title: "Pedido de amizade",
    body: `${actor} quer provar vinho contigo.`,
  }),
  group_invite: ({ actor, group }) => ({
    title: `Convite para ${group ?? "um grupo"}`,
    body: `${actor} convida-te para ${group ?? "um grupo"}.`,
  }),
  group_join_request: ({ actor, group }) => ({
    title: `Pedido para ${group ?? "o teu grupo"}`,
    body: `${actor} quer juntar-se a ${group ?? "o teu grupo"}.`,
  }),
  session_invite: ({ actor, session }) => ({
    title: "Convite para uma prova",
    body: `${actor} convida-te para ${session ?? "uma prova"}.`,
  }),
  session_reminder: ({ session }) => ({
    title: "Prova dentro de uma hora",
    body: `${session ?? "A tua prova"} começa dentro de uma hora.`,
  }),
  session_starting: ({ session }) => ({
    title: "A tua prova vai começar",
    body: `${session ?? "A tua prova"} começa agora.`,
  }),
  session_moved: ({ session }) => ({
    title: "Nova hora para uma prova",
    body: `${session ?? "A tua prova"} foi adiada.`,
  }),
  session_cancelled: ({ session }) => ({
    title: "Prova cancelada",
    body: `${session ?? "A tua prova"} não vai realizar-se.`,
  }),
  activity_tasting_now: ({ actor, session }) => ({
    title: `${actor} está a provar agora`,
    body: `${actor} está a provar ${session ?? "com outras pessoas"} neste momento.`,
  }),
  group_shop_tick: ({ actor, group }) => ({
    title: group ?? "um grupo",
    body: `${actor} tem um vinho na lista.`,
  }),
};

const TEXTS: Readonly<Record<PushLang, Readonly<Record<PushKind, PushText>>>> = {
  nl: NL,
  en: EN,
  de: DE,
  fr: FR,
  it: IT,
  es: ES,
  pt: PT,
};

/** A social push in the recipient's language, in our own words. */
export function socialPushText(
  kind: PushKind,
  lang: PushLang,
  names: SocialPushNames,
): PushPayload {
  // nosemgrep: rules.javascript.lang.security.audit.unsafe-dynamic-method -- lang and kind are closed unions, so the lookups can only reach one of the builders above.
  return TEXTS[lang][kind](names);
}
